/**
 * @file ChatService.tsx
 * @author Arunkumar Annamalai
 * @version 1.0.0
 * @description Chat Service page for managing chat tokens and API keys.
 *              This page provides a data table for viewing, adding, editing, and deleting chat tokens.
 *              It includes modal dialogs for token management operations.
 * @created Aug-2025
 * @lastModified Aug-2025
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useCompanies } from '../hooks/useCompanies';
import { useToast } from '../contexts/ToastContext';
import { ChatToken } from '../types';
import { chatServiceAPI } from '../services/chatServiceAPI';
import Modal from '../components/Modal';
import { getTableHeaderStyles, getTableContainerStyles, getTableRowStyles, getTableCellStyles } from '../styles/tableStyles';
import { formatTableDate } from '../utils/dateUtils';

// Import chat service images
import chatgptIcon from '../assets/chatgpt.png';
import openaiIcon from '../assets/openai.png';
import geminiIcon from '../assets/gemini.png';

// Fallback for missing images
const getServiceIcon = (serviceName: string): string | undefined => {
  if (!serviceName) return undefined;
  
  try {
    switch (serviceName) {
      case 'Chat GPT':
        return chatgptIcon;
      case 'Azure OpenAI':
        return openaiIcon;
      case 'Gemini':
        return geminiIcon;
      default:
        return undefined;
    }
  } catch (error) {
    console.warn('Service icon not found for:', serviceName);
    return undefined;
  }
};

// No mock data needed - will use real API

const ChatService: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { companies, loading: companiesLoading } = useCompanies();
  const { showToast } = useToast();
  // const { user } = useAuth(); // Reserved for future use
  
  const [chatTokens, setChatTokens] = useState<ChatToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openViewTokenModal, setOpenViewTokenModal] = useState(false);
  const [selectedToken, setSelectedToken] = useState<ChatToken | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    chatService: 'Chat GPT',
    apiToken: '',
    companyId: '',
  });
  const [currentStep, setCurrentStep] = useState<'token' | 'company'>('token');
  const [validating, setValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch chat tokens on component mount
  const fetchChatTokens = useCallback(async () => {
    try {
      setLoading(true);
      
      // Use the regular tokens endpoint for all users
      // The backend will handle authorization and return appropriate tokens
      const tokens = await chatServiceAPI.getChatTokens();
      
      if (tokens && Array.isArray(tokens)) {
        setChatTokens(tokens);
      } else {
        console.warn('API returned non-array or null tokens:', tokens);
        setChatTokens([]);
      }
      
    } catch (error) {
      console.error('Error fetching chat tokens:', error);
      showToast('Failed to fetch chat tokens', 'error');
      setChatTokens([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchChatTokens();
  }, [fetchChatTokens]);

  // State for available companies
  const [availableCompanies, setAvailableCompanies] = useState<{ id: string; name: string }[]>([]);
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  
  // Since this page is only visible for root admin users, we can simplify the logic
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filtered tokens based on company filter (root admin can see all or filter by company)
  const filteredTokens = useMemo(() => {
    return chatTokens ? chatTokens.filter(token => {
      return companyFilter === 'all' || token.companyId === companyFilter;
    }) : [];
  }, [chatTokens, companyFilter]);

  // Debug logging to help identify the issue
  useEffect(() => {
    // Debug logging removed for production
  }, [companyFilter, chatTokens, filteredTokens]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(0);
  }, [companyFilter]);

  // Paginated tokens
  const paginatedTokens = filteredTokens ? filteredTokens.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  ) : [];

  // Handle pagination change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Fetch available companies when needed
  useEffect(() => {
    if (openAddModal && currentStep === 'company') {
      const fetchAvailableCompanies = async () => {
        try {
          const companies = await chatServiceAPI.getAvailableCompanies();
          setAvailableCompanies(companies || []);
        } catch (error) {
          console.error('Error fetching available companies', error);
          showToast('Failed to fetch available companies', 'error');
          setAvailableCompanies([]);
        }
      };
      fetchAvailableCompanies();
    }
  }, [openAddModal, currentStep, showToast]);

  const handleAddNew = () => {
    setFormData({ name: '', chatService: 'Chat GPT', apiToken: '', companyId: '' });
    setCurrentStep('token');
    setOpenAddModal(true);
  };

  const handleEdit = (token: ChatToken) => {
    setSelectedToken(token);
    setFormData({
      name: token.name,
      chatService: token.chatService,
      apiToken: token.apiToken || '',
      companyId: token.companyId || '',
    });
    setOpenEditModal(true);
  };

  const handleDelete = (token: ChatToken) => {
    setSelectedToken(token);
    setOpenDeleteModal(true);
  };

  const handleViewToken = (token: ChatToken) => {
    setSelectedToken(token);
    setOpenViewTokenModal(true);
  };



  const handleCloseModal = () => {
    setOpenAddModal(false);
    setOpenEditModal(false);
    setOpenDeleteModal(false);
    setOpenViewTokenModal(false);
    setSelectedToken(null);
    setFormData({ name: '', chatService: 'Chat GPT', apiToken: '', companyId: '' });
    setCurrentStep('token');
    setValidating(false);
    setIsSaving(false);
  };

  const handleInputChange = (field: string, value: string) => {
    if (!field || typeof value !== 'string') return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCompanySelect = (companyId: string) => {
    if (!companyId) return;
    setFormData(prev => ({ ...prev, companyId }));
    // Don't save the token here - wait for user to click SAVE TOKEN button
  };

  const handleValidateToken = async () => {
    if (!formData.apiToken || !formData.apiToken.trim()) {
      showToast('Please enter an API token to validate', 'error');
      return;
    }

    try {
      setValidating(true);
      
      const result = await chatServiceAPI.validateToken(
        formData.apiToken,
        formData.chatService
      );
      
      if (result && result.isValid) {
        setCurrentStep('company');
        showToast('API token validation successful! Please select a company.', 'success');
      } else {
        showToast(`Validation failed: ${result?.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error validating token:', error);
      showToast('Failed to validate API token', 'error');
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    // Prevent multiple calls
    if (isSaving) {
      return;
    }

    // Validate required fields based on mode
    if (openAddModal) {
      // Add mode: require all fields including companyId
      if (!formData.name || formData.name.trim() === '' || !formData.apiToken || formData.apiToken.trim() === '' || !formData.companyId) {
        showToast('Please fill in all required fields', 'error');
        return;
      }
    } else if (openEditModal) {
      // Edit mode: require name and apiToken, companyId is not editable
      if (!formData.name || formData.name.trim() === '' || !formData.apiToken || formData.apiToken.trim() === '') {
        showToast('Please fill in all required fields', 'error');
        return;
      }
    }

    // Check if API token is already in use by any company (excluding current token during edit)
    const existingToken = chatTokens ? chatTokens.find(token => 
      token.apiToken === formData.apiToken && 
      (!openEditModal || token.id !== selectedToken?.id)
    ) : null;
    
    if (existingToken) {
      showToast(`This API token is already configured for company: ${existingToken.companyName || 'Unknown Company'}`, 'error');
      return;
    }

    try {
      setIsSaving(true);
      
      if (openAddModal) {
        await chatServiceAPI.createChatToken({
          name: formData.name,
          chatService: formData.chatService || 'Chat GPT',
          apiToken: formData.apiToken,
          companyId: formData.companyId
        });
        
        // Refresh the chat tokens from server to ensure consistency
        await fetchChatTokens();
        
        showToast('Chat token added successfully!', 'success');
        handleCloseModal();
      } else if (openEditModal && selectedToken) {
        // Edit existing token
        await chatServiceAPI.updateChatToken(selectedToken.id, {
          name: formData.name,
          chatService: formData.chatService || 'Chat GPT',
          apiToken: formData.apiToken
        });
        
        // Refresh the chat tokens from server to ensure consistency
        await fetchChatTokens();
        
        showToast('Chat token updated successfully!', 'success');
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error saving chat token:', error);
      showToast('Failed to save chat token', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedToken) {
      showToast('No token selected for deletion', 'error');
      return;
    }
    
    try {
      await chatServiceAPI.deleteChatToken(selectedToken.id);
      
      // Refresh the chat tokens from server to ensure consistency
      await fetchChatTokens();
      
      showToast('Chat token deleted successfully!', 'success');
      handleCloseModal();
    } catch (error) {
      console.error('Error deleting chat token:', error);
      showToast('Failed to delete chat token', 'error');
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header: filter left, add button right (page title in Layout toolbar) */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        gap: 2
      }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Company</InputLabel>
          <Select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            label="Filter by Company"
          >
            <MenuItem value="all">All Companies</MenuItem>
            {companiesLoading ? (
              <MenuItem value="loading" disabled>
                Loading companies...
              </MenuItem>
            ) : companies && companies.length > 0 ? (
              companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="no-companies" disabled>
                No companies available
              </MenuItem>
            )}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          size="small"
        >
          Add New Token
        </Button>
      </Box>



      {/* Chat Tokens Table */}
      <Paper sx={getTableContainerStyles()}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={getTableHeaderStyles(isDarkMode)}>Token Name</TableCell>
                    <TableCell sx={getTableHeaderStyles(isDarkMode)}>Company</TableCell>
                    <TableCell sx={getTableHeaderStyles(isDarkMode)}>Chat Service</TableCell>
                    <TableCell sx={getTableHeaderStyles(isDarkMode)}>Token</TableCell>
                    <TableCell sx={getTableHeaderStyles(isDarkMode)}>Status</TableCell>
                    <TableCell sx={getTableHeaderStyles(isDarkMode)}>Created</TableCell>
                    <TableCell sx={getTableHeaderStyles(isDarkMode)}>Last Used</TableCell>
                    <TableCell sx={getTableHeaderStyles(isDarkMode)}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!chatTokens || chatTokens.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No chat tokens found. Create your first one to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredTokens.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No tokens found for the selected company filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedTokens && paginatedTokens.length > 0 ? (
                      paginatedTokens.map((token) => (
                        <TableRow key={token.id} sx={getTableRowStyles()}>
                          <TableCell sx={getTableCellStyles()}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {token.name || 'Unnamed Token'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            <Typography variant="body2">{token.companyName || 'Unknown Company'}</Typography>
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {(() => {
                                const icon = token.chatService ? getServiceIcon(token.chatService) : undefined;
                                return icon ? (
                                  <img 
                                    src={icon} 
                                    alt={token.chatService || 'Chat Service'} 
                                    style={{ width: 20, height: 20 }} 
                                  />
                                ) : null;
                              })()}
                              <Typography variant="body2">{token.chatService || 'Unknown Service'}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {token.apiToken ? 
                                `${token.apiToken.substring(0, 8)}...${token.apiToken.substring(token.apiToken.length - 4)}` : 
                                'N/A'
                              }
                            </Typography>
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            <Chip
                              label={token.isActive !== false ? 'Active' : 'Inactive'}
                              color={token.isActive !== false ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            <Typography variant="body2">
                              {formatTableDate(token.createdAt, 'created')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            <Typography variant="body2">
                              {formatTableDate(token.lastUsedAt, 'lastUsed')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewToken(token)}
                                sx={{ color: 'info.main' }}
                                title="View Details"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(token)}
                                sx={{ color: 'primary.main' }}
                                title="Edit Token"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(token)}
                                sx={{ color: 'error.main' }}
                                title="Delete Token"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No tokens available for display.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredTokens.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Add/Edit Token Modal */}
      <Modal
        open={openAddModal || openEditModal}
        onClose={handleCloseModal}
        title={openAddModal ? 
          'Add New Chat Token'
          : 'Edit Chat Token'
        }
        maxWidth="md"
        fullWidth
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            {openAddModal && currentStep === 'token' && (
              <Button
                onClick={handleValidateToken}
                variant="contained"
                disabled={!formData.apiToken || !formData.apiToken.trim() || validating}
                startIcon={validating ? <CircularProgress size={16} /> : null}
              >
                {validating ? 'Validating...' : 'Validate Token'}
              </Button>
            )}
            {openAddModal && currentStep === 'company' && (
              <>
                <Button
                  onClick={() => setCurrentStep('token')}
                  variant="outlined"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  disabled={!formData.name || !formData.name.trim() || !formData.apiToken || !formData.apiToken.trim() || !formData.companyId || isSaving}
                  startIcon={isSaving ? <CircularProgress size={16} /> : null}
                >
                  {isSaving ? 'Saving...' : 'Save Token'}
                </Button>
              </>
            )}
            {openEditModal && (
              <>
                <Button
                  onClick={handleCloseModal}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  disabled={!formData.name || !formData.name.trim() || !formData.apiToken || !formData.apiToken.trim() || isSaving}
                  startIcon={isSaving ? <CircularProgress size={16} /> : null}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            )}
          </Box>
        }
      >
        {/* Step Indicator */}
        {openAddModal && (
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              maxWidth: '400px',
              width: '100%'
            }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: currentStep === 'token' ? 'primary.main' : 'grey.300',
                  color: currentStep === 'token' ? 'white' : 'text.secondary',
                  fontWeight: 'bold',
                  fontSize: '0.875rem'
                }}
              >
                1
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: currentStep === 'token' ? 'primary.main' : 'text.secondary',
                  fontWeight: currentStep === 'token' ? 'bold' : 'normal',
                  minWidth: '80px'
                }}
              >
                Token Setup
              </Typography>
              <Box sx={{ flex: 1, height: 2, bgcolor: 'grey.300', mx: 1 }} />
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: currentStep === 'company' ? 'primary.main' : 'grey.300',
                  color: currentStep === 'company' ? 'white' : 'text.secondary',
                  fontWeight: 'bold',
                  fontSize: '0.875rem'
                }}
              >
                2
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: currentStep === 'company' ? 'primary.main' : 'text.secondary',
                  fontWeight: currentStep === 'company' ? 'bold' : 'normal',
                  minWidth: '80px'
                }}
              >
                Company Selection
              </Typography>
            </Box>
          </Box>
        )}

        {openAddModal && currentStep === 'token' && (
          // Step 1: Token Input and Validation
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Chat Service Selection - Small Size Cards */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Chat Service
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {[
                  { 
                    name: 'Chat GPT', 
                    icon: (
                      <Box
                        component="img"
                        src={chatgptIcon}
                        alt="ChatGPT"
                        sx={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    )
                  },
                  { 
                    name: 'Azure OpenAI', 
                    icon: (
                      <Box
                        component="img"
                        src={openaiIcon}
                        alt="Azure OpenAI"
                        sx={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    )
                  },
                  { 
                    name: 'Gemini', 
                    icon: (
                      <Box
                        component="img"
                        src={geminiIcon}
                        alt="Gemini"
                        sx={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    )
                  }
                ].map((service) => (
                  <Paper
                    key={service.name}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: formData.chatService === service.name ? '2px solid' : '1px solid',
                      borderColor: formData.chatService === service.name ? 'primary.main' : 'divider',
                      bgcolor: 'background.paper',
                      color: formData.chatService === service.name ? 'primary.contrastText' : 'text.primary',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                      minWidth: '120px',
                      minHeight: '80px',
                      textAlign: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                    }}
                    onClick={() => service.name && handleInputChange('chatService', service.name)}
                  >
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {service.icon}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {service.name}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
            
            <TextField
              fullWidth
              label="Chat Token Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Customer Support Bot"
            />
            <TextField
              fullWidth
              label="API Token"
              value={formData.apiToken}
              onChange={(e) => handleInputChange('apiToken', e.target.value)}
              placeholder="Enter your Chat GPT API token"
              type="password"
              helperText="Your API token will be encrypted and stored securely"
            />
          </Box>
        )}
        
        {openAddModal && currentStep === 'company' && (
          // Step 2: Company Selection
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Select Company for Chat Service
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose a company that doesn't already have a chat service configured.
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Company</InputLabel>
              <Select
                value={formData.companyId}
                onChange={(e) => handleCompanySelect(e.target.value)}
                label="Company"
              >
                {availableCompanies && availableCompanies.length > 0 ? (
                  availableCompanies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    No companies available
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            
            {(!availableCompanies || availableCompanies.length === 0) && (
              <Alert severity="info">
                All companies already have chat services configured.
              </Alert>
            )}
          </Box>
        )}
        

        
        {openEditModal && (
          // Edit Token Flow
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Chat Service</InputLabel>
              <Select
                value={formData.chatService}
                label="Chat Service"
                disabled
              >
                <MenuItem value="Chat GPT">Chat GPT</MenuItem>
                <MenuItem value="Azure OpenAI">Azure OpenAI</MenuItem>
                <MenuItem value="Gemini">Gemini</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Chat service cannot be changed after creation
              </Typography>
            </FormControl>
            <TextField
              fullWidth
              label="Chat Token Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
            <TextField
              fullWidth
              label="API Token"
              value={formData.apiToken}
              onChange={(e) => handleInputChange('apiToken', e.target.value)}
              placeholder="Enter your API token"
              type="password"
              helperText="Your API token will be encrypted and stored securely"
            />
            <TextField
              fullWidth
              label="Company"
              value={selectedToken?.companyName || 'N/A'}
              disabled
              sx={{ '& .MuiInputBase-input': { color: 'text.disabled' } }}
              helperText="Company assignment cannot be changed"
            />
          </Box>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={openDeleteModal}
        onClose={handleCloseModal}
        title="Confirm Deletion"
        maxWidth="sm"
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>

            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
            >
              Confirm Delete
            </Button>
          </Box>
        }
      >
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this API token?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            It is currently used by <strong>{selectedToken?.companyName || 'Unknown Company'}</strong>.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Alert>
        </Box>
      </Modal>

      {/* View Token Modal */}
      <Modal
        open={openViewTokenModal}
        onClose={handleCloseModal}
        title="View API Token"
        maxWidth="sm"
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button onClick={handleCloseModal} variant="outlined">
              Close
            </Button>
          </Box>
        }
      >
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Token Name:</strong> {selectedToken?.name || 'N/A'}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Company:</strong> {selectedToken?.companyName || 'N/A'}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Chat Service:</strong> {selectedToken?.chatService || 'N/A'}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>API Token:</strong> 
            <Box sx={{ 
              fontFamily: 'monospace', 
              fontSize: '0.875rem', 
              bgcolor: 'grey.100', 
              p: 1, 
              mt: 0.5, 
              borderRadius: 1,
              wordBreak: 'break-all'
            }}>
              {selectedToken?.apiToken || 'N/A'}
            </Box>
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Created:</strong> {formatTableDate(selectedToken?.createdAt, 'created')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Last Used:</strong> {formatTableDate(selectedToken?.lastUsedAt, 'lastUsed')}
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
};

export default ChatService;
